<template>
  <div class="wae-container">
    <!-- Sidebar (Left) -->
    <div class="wae-sidebar">
      <div 
        v-for="student in students" 
        :key="student.id" 
        class="wae-student-item" 
        :class="{ active: activeStudentId === student.id }"
        @click="activeStudentId = student.id"
      >
        <div class="wae-student-avatar">
          {{ student.initials }}
        </div>
        <span class="wae-student-name">{{ student.name }}</span>
        <span 
          class="wae-student-wrong-badge" 
          :class="{ 'has-wrong': student.wrongCount > 0, 'is-zero': student.wrongCount === 0 }"
          :title="`${student.wrongCount} wrong ${student.wrongCount === 1 ? 'answer' : 'answers'}`"
        >
          {{ student.wrongCount }}
        </span>
        <i v-if="student.completed" class="fa fa-check-circle wae-check-icon"></i>
      </div>
    </div>

    <!-- Main Content (Right) -->
    <div class="wae-content">
      <!-- Back Header -->
      <div class="wae-header">
        <div class="wae-back-btn" @click="$emit('close')">
          <i class="fa fa-arrow-left"></i>
          <span>Wrong Answer Evaluation</span>
        </div>
        <p class="wae-subtitle">Question Performance Overview &amp; Student Engagement Metrics</p>
      </div>

      <!-- Category Pills -->
      <div class="wae-categories">
        <div 
          v-for="cat in computedCategories" 
          :key="cat.name" 
          class="wae-cat-pill" 
          :class="[cat.class, { active: activeCategory === cat.name }]"
          @click="activeCategory = cat.name"
        >
          {{ cat.name }}
          <span v-if="cat.count > 0" class="wae-cat-count">{{ cat.count }}</span>
        </div>
      </div>

      <!-- Table Section -->
      <div v-if="loading" class="wae-loading-state">
        <i class="fa fa-spinner fa-spin fa-3x" style="color: #3b82f6; margin-bottom: 12px;"></i>
        <p style="margin: 0; font-size: 14px; color: #94a3b8;">Fetching student answers...</p>
      </div>

      <div v-else-if="activeTasks.length > 0" class="wae-table-card">
        <table class="wae-table">
          <thead>
            <tr>
              <th style="text-align: left; width: 40%;">QUESTION</th>
              <th style="text-align: center; width: 20%;">STUDENT'S ANSWER</th>
              <th style="text-align: center; width: 20%;">COMPLETION STATUS</th>
              <th style="text-align: right; width: 20%; padding-right: 24px;">ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in activeTasks" :key="task.id">
              <td class="wae-col-question">{{ task.questionText }}</td>
              <td class="wae-col-answer">
                <button class="wae-draft-btn" @click="handleViewDraft(task)">
                  View Draft
                </button>
              </td>
              <td class="wae-col-status">
                <span class="wae-status-badge" :class="task.status.toLowerCase()">
                  {{ task.status }}
                </span>
              </td>
              <td class="wae-col-action" style="padding-right: 24px;">
                <button 
                  v-if="task.status === 'Pending'" 
                  class="wae-action-btn dark"
                  @click="handleSelectTags(task)"
                >
                  Select Tags
                </button>
                <button 
                  v-else 
                  class="wae-action-btn outline"
                  @click="handleReviewTags(task)"
                >
                  Review Tags
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="wae-empty-state">
        <div class="wae-empty-icon">
          <i class="fa fa-check-circle"></i>
        </div>
        <h3 class="wae-empty-title">No Wrong Answers</h3>
        <p class="wae-empty-desc">This student has not given any wrong answers{{ activeCategory ? ' in the ' + activeCategory + ' category' : '' }}.</p>
      </div>
    </div>

    <!-- Reasoning Tag Dialog -->
    <ReasoningTagDialog
      :visible="showReasoningTagDialog"
      :sid="computedSid"
      :student-id="activeStudentId"
      :question-id="activeTaskForTag ? activeTaskForTag.questionId : ''"
      :abstract-data="activeTaskForTag ? activeTaskForTag.questionText : ''"
      @saved="handleReasoningTagSaved"
      @close="showReasoningTagDialog = false" />

    <!-- Review Tags Popup -->
    <div v-if="showReviewTagsPopup" class="wae-review-overlay" @click.self="showReviewTagsPopup = false">
      <div class="wae-review-card">
        <div class="wae-review-header">
          <div class="wae-review-title">Assigned Reasoning Tags</div>
          <i class="el-icon-close wae-review-close" @click="showReviewTagsPopup = false"></i>
        </div>
        <div class="wae-review-body">
          <div v-if="reviewTagsForTask.length > 0">
            <div v-for="(tag, idx) in reviewTagsForTask" :key="idx" class="wae-review-tag-item">
              <span class="wae-review-tag-dot" :style="{ backgroundColor: getTagColor(tag.reasoningTag) }"></span>
              <span class="wae-review-tag-label">{{ tag.reasoningTag }}</span>
              <span class="wae-review-tag-date" v-if="tag.createdAt">{{ formatDate(tag.createdAt) }}</span>
            </div>
          </div>
          <div v-else class="wae-review-empty">No tags assigned yet.</div>
        </div>

      </div>
    </div>

    <!-- Student Answer Popup -->
    <div v-if="showAnswerPopup" class="wae-review-overlay" @click.self="showAnswerPopup = false">
      <div class="wae-review-card">
        <div class="wae-review-header">
          <div class="wae-review-title">Student's Answer</div>
          <i class="el-icon-close wae-review-close" @click="showAnswerPopup = false"></i>
        </div>
        <div class="wae-review-body" style="padding: 24px; min-height: 100px;">
          <p style="margin: 0; font-size: 15px; color: #334155; line-height: 1.5; white-space: pre-wrap;">{{ activeAnswerText || 'No text answer given by student' }}</p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import ReasoningTagDialog from './ReasoningTagDialog.vue'

const AVATAR_COLORS = [
  '#10b981', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6',
  '#14b8a6', '#f97316', '#06b6d4', '#e11d48', '#8b5cf6', '#84cc16',
  '#a855f7', '#0ea5e9', '#ef4444', '#22c55e', '#eab308', '#6d28d9'
]

const BLOOM_LABELS = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create']

export default {
  name: 'WrongAnswerEvaluation',
  components: { ReasoningTagDialog },
  props: {
    slides: {type: Array, default: () => []},
    gameOnRecord: {type: Object, default: () => null},
    activityLogs: {type: Array, default: () => []},
    apiResponses: {type: Array, default: () => []},
    apiQuestions: {type: Array, default: () => []},
    reasoningTags: {type: Array, default: () => []},
  },

  data() {
    return {
      activeStudentId: '',
      activeCategory: 'Remember',
      categories: [
        { name: 'Remember', class: 'cat-remember' },
        { name: 'Understand', class: 'cat-understand' },
        { name: 'Apply', class: 'cat-apply' },
        { name: 'Analyze', class: 'cat-analyze' },
        { name: 'Create', class: 'cat-create' },
        { name: 'Evaluate', class: 'cat-evaluate' },
      ],
      // Local fallback data
      localGameOnRecord: null,
      localActivityLogs: [],
      localApiResponses: [],
      localApiQuestions: [],
      localReasoningTags: [],
      
      showReasoningTagDialog: false,
      activeTaskForTag: null,
      showReviewTagsPopup: false,
      reviewTagsForTask: [],
      // Student Answer Popup
      showAnswerPopup: false,
      activeAnswerText: '',
      loading: false,
    }
  },
  computed: {
    computedCategories() {
      if (!this.activeStudentId) {
        return this.categories.map(c => ({ ...c, count: 0 }))
      }
      const wrongQuestions = this.getWrongQuestionsForStudent(this.activeStudentId)
      return this.categories.map(cat => {
        const bloomIndex = BLOOM_LABELS.indexOf(cat.name)
        const count = wrongQuestions.filter(q => {
          const question = this.findQuestionData(q.pageId || q.questionId)
          if (!question) return bloomIndex === -1
          const bloom = Array.isArray(question.bloom) ? question.bloom : (question.bloom != null ? [question.bloom] : [])
          if (bloom.length === 0) return bloomIndex === 0
          return bloom.includes(bloomIndex)
        }).length
        return {
          ...cat,
          count
        }
      })
    },
    computedSid() {
      return this.$store?.state?.session?.sid || ''
    },
    effectiveGameOnRecord() {
      return this.gameOnRecord || this.localGameOnRecord
    },
    effectiveActivityLogs() {
      return (this.activityLogs && this.activityLogs.length > 0) ? this.activityLogs : this.localActivityLogs
    },
    effectiveApiResponses() {
      return (this.apiResponses && this.apiResponses.length > 0) ? this.apiResponses : this.localApiResponses
    },
    effectiveApiQuestions() {
      return (this.apiQuestions && this.apiQuestions.length > 0) ? this.apiQuestions : this.localApiQuestions
    },
    effectiveReasoningTags() {
      return (this.reasoningTags && this.reasoningTags.length > 0) ? this.reasoningTags : this.localReasoningTags
    },
    allMembers() {
      const record = this.effectiveGameOnRecord
      if (!record) return []
      const members = record.members || []
      const guestMembers = record.guestMembers || []
      return [...members, ...guestMembers]
    },
    students() {
      return this.allMembers.map((m, idx) => {
        const name = this.getMemberName(m)
        const initials = this.getInitials(name)
        const uid = m.uid || m._id || m.id || ''
        const wrongQuestions = this.getWrongQuestionsForStudent(uid)
        const evaluatedCount = wrongQuestions.filter(q => this.isQuestionEvaluated(uid, q.questionId || q.pageId)).length
        const completed = wrongQuestions.length > 0 && evaluatedCount >= wrongQuestions.length

        return {
          id: uid,
          name: name,
          initials: initials,
          color: AVATAR_COLORS[idx % AVATAR_COLORS.length],
          completed: completed,
          wrongCount: wrongQuestions.length,
        }
      })
    },
    sessionQuestions() {
      const session = this.$store?.state?.session
      return (session && session.questions) ? session.questions : []
    },
    activeTasks() {
      if (!this.activeStudentId) return []

      const wrongQuestions = this.getWrongQuestionsForStudent(this.activeStudentId)

      // Filter by bloom category
      const bloomIndex = BLOOM_LABELS.indexOf(this.activeCategory)

      const filtered = wrongQuestions.filter(q => {
        const question = this.findQuestionData(q.pageId || q.questionId)
        if (!question) return bloomIndex === -1
        const bloom = Array.isArray(question.bloom) ? question.bloom : (question.bloom != null ? [question.bloom] : [])
        if (bloom.length === 0) return bloomIndex === 0
        return bloom.includes(bloomIndex)
      })

      return filtered.map((q, idx) => {
        const question = this.findQuestionData(q.pageId || q.questionId)
        const qId = q.questionId || q.pageId
        const isEvaluated = this.isQuestionEvaluated(this.activeStudentId, qId)
        
        let questionText = ''
        if (question) {
          questionText = (question.abstract && question.abstract.data) || (question.cpa && question.cpa.outcome) || question.abstractData || question.title || question.data
          if (typeof questionText === 'string') {
            questionText = questionText.replace(/<[^>]*>/g, '').trim()
          }
          if (question.isSubQuestion) {
            const levelNum = (question.bloom && question.bloom.length > 0) ? (question.bloom[0] + 1) : ''
            if (questionText) {
              questionText = `${questionText} (Level ${levelNum})`
            } else {
              questionText = `Level ${levelNum} Question`
            }
          }
        }
        
        if (!questionText) {
          // Prevent log text like "Yash attempted... incorrectly" from showing up as the question
          if (q.text && !q.text.includes('attempted') && !q.text.includes('is stuck') && !q.text.includes('Bloom level')) {
            questionText = q.text
          } else {
            questionText = `Question ${idx + 1}`
          }
        }

        return {
          id: qId + '-' + idx,
          questionId: qId,
          pageId: q.pageId,
          questionText: questionText,
          status: isEvaluated ? 'Complete' : 'Pending',
          studentAnswer: q.studentAnswer || '',
        }
      })
    },
  },
  watch: {
    students: {
      handler(val) {
        if (val && val.length > 0 && !this.activeStudentId) {
          this.activeStudentId = val[0].id
        }
      },
      immediate: true,
    },
    activeStudentId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.activeCategory = 'Remember'
      }
    },
    effectiveActivityLogs: {
      handler(val) {
        console.log('[WAE] All activity logs:', val)
      },
      immediate: true
    }
  },
  async created() {
    await this.initData()
  },
  methods: {
    async initData() {
      this.loading = true
      try {
        const sid = this.computedSid
        if (!sid || !window.App) return

        console.log('[WAE] initData checking for missing props. props.activityLogs:', this.activityLogs ? this.activityLogs.length : 0)

        // 1. Fetch gameOnRecord if missing
        if (!this.gameOnRecord) {
          console.log('[WAE] gameOnRecord prop missing, fetching from API...')
          const res = await App.service('game-on').find({
            query: { sid, $limit: 1, $sort: { createdAt: -1 } },
          })
          const records = res.data || res || []
          if (records.length > 0) {
            this.localGameOnRecord = records[0]
          }
        }

        // 2. Fetch responses if missing
        if (!this.apiResponses || this.apiResponses.length === 0) {
          console.log('[WAE] apiResponses prop missing, fetching from API...')
          const res = await App.service('response').find({
            query: { sid, $limit: 10000, $sort: { createdAt: 1 } },
          })
          this.localApiResponses = res.data || res || []
        }

        // 3. Fetch questions if missing
        const uniqueQIds = [...new Set(this.effectiveApiResponses.map(r => r.questionId || r.page).filter(Boolean))]
        if ((!this.apiQuestions || this.apiQuestions.length === 0) && uniqueQIds.length > 0) {
          console.log('[WAE] apiQuestions prop missing, fetching from API...')
          const res = await App.service('questions').find({
            query: { _id: { $in: uniqueQIds }, $limit: 2000 },
          })
          this.localApiQuestions = res.data || res || []
        }

        // 4. Fetch activityLogs if missing
        if (!this.activityLogs || this.activityLogs.length === 0) {
          const recordId = this.effectiveGameOnRecord && (this.effectiveGameOnRecord._id || this.effectiveGameOnRecord.id)
          if (recordId) {
            console.log('[WAE] activityLogs prop missing, fetching from API for recordId:', recordId)
            const res = await App.service('game-on-log').find({
              query: { gameId: recordId, $limit: 1000 },
            })
            this.localActivityLogs = res.data || res || []
            console.log('[WAE] Fetched activity logs count:', this.localActivityLogs.length)
          } else {
            console.log('[WAE] Cannot fetch activityLogs: no recordId found')
          }
        }

        // 5. Fetch reasoning tags if missing
        if (!this.reasoningTags || this.reasoningTags.length === 0) {
          await this.fetchReasoningTags()
        }
      } catch (e) {
        console.error('[WAE] initData fetch failed:', e)
      } finally {
        this.loading = false
      }
    },
    async fetchReasoningTags() {
      if (!window.App) return
      const sid = this.computedSid
      if (!sid) return
      
      const uniqueQIds = [...new Set(this.effectiveApiResponses.map(r => r.questionId || r.page).filter(Boolean))]
      const query = { sid, $limit: 5000 }
      
      if (uniqueQIds.length > 0) {
        query.questionid = { $in: uniqueQIds }
      }

      try {
        console.log('[WAE] reasoningTags prop missing, fetching from API...')
        const res = await App.service('student-reasoning-tag').find({ query })
        this.localReasoningTags = res.data || res || []
      } catch (e) {
        console.error('[WAE] Failed to fetch reasoning tags:', e)
      }
    },
    getMemberName(member) {
      if (member.nickname) return member.nickname
      if (member.name) {
        if (Array.isArray(member.name)) {
          return member.name.filter(Boolean).join(' ')
        }
        return member.name
      }
      return member.uid || 'Unknown'
    },
    getInitials(name) {
      if (!name) return '??'
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    getWrongQuestionsForStudent(uid) {
      if (!uid) return []

      const member = this.allMembers.find(m => String(m.uid || m._id || m.id) === String(uid))
      if (!member) return []

      // Find all responses for this student
      const studentResponses = this.effectiveApiResponses.filter(r => 
        String(r.uid) === String(uid) || String(r.studentId) === String(uid) || String(r.userId) === String(uid) || String(r.peer) === String(uid) || String(r.name) === String(uid)
      )
      
      // Group by questionId or pageId to find the LATEST attempt status
      const latestAttempts = {}
      studentResponses.forEach(r => {
        const qId = r.questionId || r.page
        if (qId) {
          // Since responses are sorted by createdAt: 1, we overwrite and get the latest
          latestAttempts[qId] = r
        }
      })

      // Wrong questions are those where the LATEST response is NOT correct
      const validWrongIds = Object.keys(latestAttempts).filter(id => {
        const latestResponse = latestAttempts[id]
        if (latestResponse.isCorrect === true) return false // They got it right!
        
        const qData = this.findQuestionData(id)
        if (!qData) return false // Exclude completely missing/ghost questions so they don't break the 'completed' count
        
        // If it's a parent container that has bloom_questions, skip it
        // because we only evaluate its sub-questions.
        const hasBloomQuestions = qData.bloom_questions || (qData.cpa && qData.cpa.bloom_questions)
        if (hasBloomQuestions && !qData.isSubQuestion) {
           return false
        }
        
        return true
      })

      return validWrongIds.map(id => {
        const qData = this.findQuestionData(id)
        const parentId = qData && qData.isSubQuestion ? qData.parentPageId : null
        
        const responseData = latestAttempts[id]

        // Format student answer nicely
        let answerStr = ''
        if (responseData) {
          if (Array.isArray(responseData.answer)) answerStr = responseData.answer.join(', ')
          else if (responseData.answer) answerStr = responseData.answer
          else if (responseData.content) answerStr = responseData.content
        }

        // Try to find activity log text as fallback
        let logText = ''
        const log = this.effectiveActivityLogs.find(l => {
          if (String(l.uid) !== String(uid)) return false
          if (String(l.pageId) === String(id) || String(l.questionId) === String(id)) return true
          if (parentId && String(l.pageId) === String(parentId) && l.text && qData && qData.bloom && qData.bloom.length > 0) {
            if (l.text.includes(`Bloom level ${qData.bloom[0] + 1}`)) return true
          }
          return false
        })
        if (log && log.text) logText = log.text

        return {
          pageId: id,
          questionId: id,
          studentAnswer: answerStr || (log ? log.studentAnswer : ''),
          text: logText,
        }
      })
    },
    findQuestionData(pageId) {
      if (!pageId) return null
      
      // 1. Try questions API data first!
      let apiFound = this.apiQuestions.find(q => String(q.id) === String(pageId) || String(q._id) === String(pageId) || String(q.page) === String(pageId))
      if (apiFound) {
        return apiFound
      }

      // 2. Try session questions
      let found = this.sessionQuestions.find(q =>
        String(q._id) === String(pageId) || String(q.id) === String(pageId) || String(q.page) === String(pageId)
      )
      if (!found) {
        // Try slides
        found = this.slides.find(s =>
          String(s._id) === String(pageId) || String(s.id) === String(pageId) || String(s.page) === String(pageId) || String(s.pageId) === String(pageId)
        )
      }
      if (found) return found

      // 3. Try sub-questions in bloom_questions
      for (const q of this.sessionQuestions) {
        const bq = q.bloom_questions || (q.cpa && q.cpa.bloom_questions)
        if (bq) {
          const entry = Object.entries(bq).find(([key, qId]) => String(qId) === String(pageId))
          if (entry) {
            return {
              ...q,
              bloom: [parseInt(entry[0])],
              isSubQuestion: true,
              parentPageId: q._id || q.id || q.page
            }
          }
        }
      }
      for (const s of this.slides) {
        const bq = s.bloom_questions || (s.cpa && s.cpa.bloom_questions)
        if (bq) {
          const entry = Object.entries(bq).find(([key, qId]) => String(qId) === String(pageId))
          if (entry) {
            return {
              ...s,
              bloom: [parseInt(entry[0])],
              isSubQuestion: true,
              parentPageId: s._id || s.id || s.page || s.pageId
            }
          }
        }
      }

      return null
    },
    getReasoningTagsForQuestion(uid, questionId) {
      if (!uid || !questionId) return []
      return this.effectiveReasoningTags.filter(t => 
        (String(t.uid) === String(uid) || String(t.studentId) === String(uid) || String(t.userId) === String(uid)) &&
        (String(t.questionid) === String(questionId) || String(t.questionId) === String(questionId))
      )
    },
    isQuestionEvaluated(uid, questionId) {
      if (!uid || !questionId) return false
      const tags = this.getReasoningTagsForQuestion(uid, questionId)
      return tags.length > 0
    },
    handleViewDraft(task) {
      this.activeAnswerText = task.studentAnswer
      this.showAnswerPopup = true
    },
    handleSelectTags(task) {
      this.activeTaskForTag = task
      this.showReasoningTagDialog = true
    },
    async handleReasoningTagSaved(selectedTag) {
      this.showReasoningTagDialog = false
      // Tell parent to refresh the reasoning tags prop
      this.$emit('refresh-tags')
    },
    handleReviewTags(task) {
      const tags = this.getReasoningTagsForQuestion(this.activeStudentId, task.questionId)
      this.reviewTagsForTask = tags
      this.showReviewTagsPopup = true
    },
    getTagColor(tagName) {
      const colorMap = {
        'Careless Calculation': '#e53e3e',
        'Misreading / Text Comprehension': '#ed8936',
        'Weak Logic': '#ecc94b',
        'Concept Unclear': '#dd6b20',
      }
      return colorMap[tagName] || '#64748b'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
  }
}
</script>

<style scoped>
.wae-container {
  display: flex;
  width: 100%;
  height: 100%;
  background: #f8fafc;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
}

/* Sidebar (Left) */
.wae-sidebar {
  width: 260px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  padding: 24px 12px;
  gap: 8px;
  height: 100%;
  overflow-y: auto;
}

.wae-student-item {
  display: flex;
  align-items: center;
  padding: 12px 16px 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  gap: 12px;
  position: relative;
  overflow: hidden;
}

.wae-student-item:hover:not(.active) {
  background: #f8fafc;
}

.wae-student-item.active {
  background: #f0fdf4;
}

.wae-student-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #10b981;
}

.wae-student-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #475569;
  background-color: #e2e8f0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.wae-student-item.active .wae-student-avatar {
  background-color: #10b981;
  color: #ffffff;
}

.wae-student-name {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wae-student-item.active .wae-student-name {
  color: #0f172a;
  font-weight: 600;
}

.wae-student-wrong-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  line-height: 16px;
  flex-shrink: 0;
  transition: all 0.2s;
  box-sizing: border-box;
}

.wae-student-wrong-badge.has-wrong {
  background-color: #fee2e2;
  color: #ef4444;
}

.wae-student-wrong-badge.is-zero {
  background-color: #f1f5f9;
  color: #94a3b8;
  font-weight: 500;
}

.wae-student-item.active .wae-student-wrong-badge.has-wrong {
  background-color: #ef4444;
  color: #ffffff;
}

.wae-student-item.active .wae-student-wrong-badge.is-zero {
  background-color: #e2e8f0;
  color: #64748b;
}

.wae-check-icon {
  color: #10b981;
  font-size: 14px;
  flex-shrink: 0;
}

/* Main Content (Right) */
.wae-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 60px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

.wae-header {
  margin-bottom: 24px;
  width: 100%;
  max-width: 800px;
}

.wae-back-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: #0f172a;
  font-size: 24px;
  font-weight: 600;
  user-select: none;
}

.wae-back-btn span {
  font-size: inherit !important;
  font-weight: inherit !important;
}

.wae-back-btn i {
  font-size: 24px !important;
  transition: transform 0.2s;
}

.wae-back-btn:hover i {
  transform: translateX(-4px);
}

.wae-subtitle {
  margin: 4px 0 0 32px;
  font-size: 13px;
  font-weight: 400;
  color: #64748b;
}

/* Category Pills */
.wae-categories {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 800px;
}

.wae-cat-pill {
  position: relative;
  padding: 7px 15px;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.wae-cat-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.wae-cat-pill:hover {
  transform: translateY(-1px);
}

/* Category Specific Styling */
.cat-remember {
  background: #E1EEF6;
  color: #0C67A8;
}
.cat-remember.active {
  background: #0C67A8;
  border-color: #245273;
  color: #ffffff;
}

.cat-understand {
  background: #F3E2F9;
  color: #8D3694;
}
.cat-understand.active {
  background: #8D3694;
  border-color: #722578;
  color: #ffffff;
}

.cat-apply {
  background: #F7E5D0;
  color: #8A4A0A;
}
.cat-apply.active {
  background: #8A4A0A;
  border-color: #6D3804;
  color: #ffffff;
}

.cat-analyze {
  background: #E6F5CE;
  color: #4A8F08;
}
.cat-analyze.active {
  background: #4A8F08;
  border-color: #376C03;
  color: #ffffff;
}

.cat-create {
  background: #FAF0C7;
  color: #8F7508;
}
.cat-create.active {
  background: #8F7508;
  border-color: #715C03;
  color: #ffffff;
}

.cat-evaluate {
  background: #E1D5FB;
  color: #5927B5;
}
.cat-evaluate.active {
  background: #5927B5;
  border-color: #451B93;
  color: #ffffff;
}

/* Table Section */
.wae-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
  max-width: 800px;
}

.wae-table {
  width: 100%;
  border-collapse: collapse;
}

.wae-table th {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 10px;
  font-weight: 600;
  color: #64748B;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.wae-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.wae-table tr:last-child td {
  border-bottom: none;
}

.wae-col-question {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  line-height: 1.5;
}

.wae-col-answer {
  text-align: center;
}

.wae-draft-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-weight: 600;
  font-size: 11px;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.wae-draft-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #1e293b;
}

.wae-col-status {
  text-align: center;
}

.wae-status-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 12px;
  display: inline-block;
}

.wae-status-badge.pending {
  background: #f1f5f9;
  color: #64748b;
}

.wae-status-badge.complete {
  background: #d1fae5;
  color: #065f46;
}

.wae-col-action {
  text-align: right;
}

.wae-action-btn {
  font-weight: 600;
  font-size: 12px;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-block;
}

.wae-action-btn.dark {
  background: #0f172a;
  color: #ffffff;
  border: none;
}

.wae-action-btn.dark:hover {
  background: #1e293b;
}

.wae-action-btn.outline {
  background: transparent;
  border: 1px solid #1e293b;
  color: #1e293b;
}

.wae-action-btn.outline:hover {
  background: #f8fafc;
  border-color: #0f172a;
  color: #0f172a;
}

/* Empty State */
.wae-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  width: 100%;
  max-width: 800px;
}

.wae-loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
}

.wae-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #d1fae5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.wae-empty-icon i {
  font-size: 36px;
  color: #10b981;
}

.wae-empty-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.wae-empty-desc {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 360px;
  line-height: 1.5;
}

/* Review Tags Popup */
.wae-review-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000010;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.wae-review-card {
  background: #ffffff;
  border-radius: 16px;
  width: 480px;
  max-width: 95vw;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: waeScaleIn 0.25s ease-out;
}

@keyframes waeScaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.wae-review-header {
  background: #ffffff;
  color: #0f172a;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px 24px;
  position: relative;
}

.wae-review-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.wae-review-close {
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 18px;
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s;
}

.wae-review-close:hover {
  color: #0f172a;
}

.wae-review-body {
  padding: 20px 24px;
  max-height: 50vh;
  overflow-y: auto;
}

.wae-review-tag-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 10px;
}

.wae-review-tag-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.wae-review-tag-label {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.wae-review-tag-date {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}

.wae-review-empty {
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  padding: 24px;
}

.wae-review-footer {
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.wae-review-close-btn {
  padding: 10px 24px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.wae-review-close-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

/* Responsive CSS */
@media (max-width: 992px) {
  .wae-container {
    flex-direction: column;
  }
  .wae-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    flex-direction: row;
    overflow-x: auto;
    padding: 16px;
  }
  .wae-student-item {
    flex-shrink: 0;
  }
}
</style>
